import { BuildingOffice2Icon, UserIcon } from '@heroicons/react/24/outline';
import type { ComponentType } from 'react';
import { DmField, DmSelect, dmInputClasses, dmInputErrorClasses } from '../../dm/DmFormPrimitives';
import type { ShipToTarget } from '../../../../../services/orthopulseOrder';
import type { ShipToFormValues } from './shipToTypes';
import type { ShipToFormErrors } from './shipToValidation';
import {
    COUNTRY_OPTIONS,
    normalizeCountry,
    postalFieldLabel,
    regionFieldLabel,
    regionOptionsForCountry,
    type ShipToCountry
} from './shipToRegions';

interface ShipToTargetOption {
    id: ShipToTarget;
    label: string;
    icon: ComponentType<{ className?: string }>;
}

const TARGET_OPTIONS: ShipToTargetOption[] = [
    { id: 'practice', label: 'Ship to practice', icon: BuildingOffice2Icon },
    { id: 'patient', label: 'Ship to patient', icon: UserIcon }
];

interface ShipToTargetPickerProps {
    target: ShipToTarget;
    onChange: (target: ShipToTarget) => void;
}

function ShipToTargetPicker({ target, onChange }: ShipToTargetPickerProps) {
    return (
        <div role="radiogroup" aria-label="Ship to" className="grid grid-cols-2 gap-2">
            {TARGET_OPTIONS.map((option) => {
                const Icon = option.icon;
                const isActive = option.id === target;
                return (
                    <button
                        key={option.id}
                        type="button"
                        role="radio"
                        aria-checked={isActive}
                        onClick={() => onChange(option.id)}
                        className={`h-11 rounded-xl border px-4 flex items-center justify-center gap-2 text-sm font-semibold transition-colors ${
                            isActive
                                ? 'border-gray-800 bg-white text-gray-900'
                                : 'border-gray-200 bg-white text-gray-600 hover:border-gray-400'
                        }`}
                    >
                        <Icon className="h-4 w-4" />
                        {option.label}
                    </button>
                );
            })}
        </div>
    );
}

function PatientAddressHint() {
    return (
        <p className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
            Patients don&apos;t have an address on file. Enter it below. Add an email and phone
            number so the vendor can send direct-shipping updates.
        </p>
    );
}

interface ShipToFieldInputProps {
    value: string;
    onChange: (value: string) => void;
    error?: boolean;
}

function ShipToFieldInput({ value, onChange, error }: ShipToFieldInputProps) {
    return (
        <input
            type="text"
            className={error ? dmInputErrorClasses : dmInputClasses}
            value={value}
            onChange={(e) => onChange(e.target.value)}
        />
    );
}

function updateField(
    values: ShipToFormValues,
    field: keyof ShipToFormValues,
    fieldValue: string
): ShipToFormValues {
    return { ...values, [field]: fieldValue };
}

// The unmatched-value notice only makes sense while the field is still empty
// - once the operator picks a state it's no longer relevant.
function resolveStateError(
    fieldError: string | undefined,
    unmatchedState: string | null | undefined,
    stateValue: string,
    country: ShipToCountry
): string | undefined {
    if (!unmatchedState || stateValue.trim()) return fieldError;
    const label = regionFieldLabel(country).toLowerCase();
    return `Stored value '${unmatchedState}' isn't a recognized ${label}. Please select one.`;
}

type FieldChangeHandler = (field: keyof ShipToFormValues) => (value: string) => void;

interface NameAddressFieldsProps {
    values: ShipToFormValues;
    errors: ShipToFormErrors;
    onFieldChange: FieldChangeHandler;
}

function NameAddressFields({ values, errors, onFieldChange }: NameAddressFieldsProps) {
    return (
        <>
            <DmField label="Name" required error={errors.name} className="col-span-2">
                <ShipToFieldInput
                    value={values.name}
                    onChange={onFieldChange('name')}
                    error={!!errors.name}
                />
            </DmField>
            <DmField label="Address" required error={errors.address} className="col-span-2">
                <ShipToFieldInput
                    value={values.address}
                    onChange={onFieldChange('address')}
                    error={!!errors.address}
                />
            </DmField>
            <DmField label="Address 2" className="col-span-2">
                <ShipToFieldInput value={values.address2} onChange={onFieldChange('address2')} />
            </DmField>
            <DmField label="City" required error={errors.city}>
                <ShipToFieldInput
                    value={values.city}
                    onChange={onFieldChange('city')}
                    error={!!errors.city}
                />
            </DmField>
        </>
    );
}

interface RegionFieldsProps {
    values: ShipToFormValues;
    errors: ShipToFormErrors;
    country: ShipToCountry;
    stateError: string | undefined;
    onFieldChange: FieldChangeHandler;
    onCountryChange: (country: string) => void;
}

function RegionFields({
    values,
    errors,
    country,
    stateError,
    onFieldChange,
    onCountryChange
}: RegionFieldsProps) {
    const stateOptions = [
        { value: '', label: 'Select state' },
        ...regionOptionsForCountry(country)
    ];

    return (
        <>
            <DmField label={regionFieldLabel(country)} required error={stateError}>
                <DmSelect
                    value={values.state}
                    onChange={onFieldChange('state')}
                    options={stateOptions}
                    error={!!stateError}
                />
            </DmField>
            <DmField label={postalFieldLabel(country)} required error={errors.zip}>
                <ShipToFieldInput
                    value={values.zip}
                    onChange={onFieldChange('zip')}
                    error={!!errors.zip}
                />
            </DmField>
            <DmField label="Country" required error={errors.country}>
                <DmSelect
                    value={values.country}
                    onChange={onCountryChange}
                    options={COUNTRY_OPTIONS}
                    error={!!errors.country}
                />
            </DmField>
        </>
    );
}

interface ContactFieldsProps {
    values: ShipToFormValues;
    errors: ShipToFormErrors;
    hint?: string;
    onFieldChange: FieldChangeHandler;
}

function ContactFields({ values, errors, hint, onFieldChange }: ContactFieldsProps) {
    return (
        <>
            <DmField label="Email" error={errors.email} hint={hint}>
                <ShipToFieldInput
                    value={values.email}
                    onChange={onFieldChange('email')}
                    error={!!errors.email}
                />
            </DmField>
            <DmField label="Phone" hint={hint}>
                <ShipToFieldInput value={values.phone} onChange={onFieldChange('phone')} />
            </DmField>
        </>
    );
}

interface ShipToFormProps {
    target: ShipToTarget;
    onTargetChange: (target: ShipToTarget) => void;
    values: ShipToFormValues;
    onValuesChange: (values: ShipToFormValues) => void;
    errors: ShipToFormErrors;
    /** Raw stored state/province that couldn't be matched to a known option -
     *  shown until the operator picks a replacement (see useShipToForms). */
    unmatchedState?: string | null;
}

function ShipToForm({
    target,
    onTargetChange,
    values,
    onValuesChange,
    errors,
    unmatchedState
}: ShipToFormProps) {
    const onFieldChange: FieldChangeHandler = (field) => (fieldValue) =>
        onValuesChange(updateField(values, field, fieldValue));

    // Country determines the state/province list and the zip/postal label -
    // switching it resets state since the old code rarely exists in the new list.
    const handleCountryChange = (nextCountry: string) => {
        onValuesChange({ ...values, country: nextCountry, state: '' });
    };

    const country = normalizeCountry(values.country);
    const stateError = resolveStateError(errors.state, unmatchedState, values.state, country);
    const contactHint =
        target === 'patient'
            ? 'Recommended so the vendor can send direct-shipping updates'
            : undefined;

    return (
        <div className="flex flex-col gap-4">
            <ShipToTargetPicker target={target} onChange={onTargetChange} />

            {target === 'patient' && <PatientAddressHint />}

            <div className="grid grid-cols-2 gap-4">
                <NameAddressFields values={values} errors={errors} onFieldChange={onFieldChange} />
                <RegionFields
                    values={values}
                    errors={errors}
                    country={country}
                    stateError={stateError}
                    onFieldChange={onFieldChange}
                    onCountryChange={handleCountryChange}
                />
                <ContactFields
                    values={values}
                    errors={errors}
                    hint={contactHint}
                    onFieldChange={onFieldChange}
                />
            </div>
        </div>
    );
}

export { ShipToForm };
